import { ComponentType } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService } from '@rms/core/notification';
import { CONFIRM_RESULT, RMS_DIALOG_MD_CONFIG } from '@rms/sdk/dialog';
import { AppError } from '@rms/sdk/errors';
import { catchError, EMPTY, filter, map, Observable, tap } from 'rxjs';

import { PartnerUser, PartnerUsersScope } from '../../models';
import { PartnerService } from '../../services';
import { PartnerUserDialogData, PartnerUserPasswordData, PartnerUserTarget } from '../partner-user-dialog.model';
import { PartnerUserDialogComponent } from '../partner-user-dialog/partner-user-dialog.component';
import { PartnerUserPasswordComponent } from '../partner-user-password/partner-user-password.component';
import { PartnerUserRoleComponent } from '../partner-user-role/partner-user-role.component';

@Injectable({
  providedIn: 'root',
})
export class PartnerUserActionsService {
  private readonly dialog = inject(MatDialog);
  private readonly notificationService = inject(NotificationService);
  private readonly partnerService = inject(PartnerService);

  public openDialog(scope: PartnerUsersScope, user?: PartnerUser): Observable<PartnerUser> {
    return this.open<PartnerUserDialogData, PartnerUser>(PartnerUserDialogComponent, { scope, user: user ?? null });
  }

  public openRoleDialog(scope: PartnerUsersScope, user: PartnerUser): Observable<PartnerUser> {
    return this.open<PartnerUserTarget, PartnerUser>(PartnerUserRoleComponent, { scope, user });
  }

  public openPasswordDialog(partnerId: string, user: PartnerUser): Observable<boolean> {
    return this.open<PartnerUserPasswordData, boolean>(PartnerUserPasswordComponent, { partnerId, user });
  }

  public confirmDelete({ full_name }: PartnerUser): Observable<void> {
    return this.notificationService
      .confirm({
        title: { text: 'partners.users.delete.title' },
        message: { text: 'partners.users.delete.message', payload: { name: full_name } },
      })
      .pipe(
        filter((result) => result === CONFIRM_RESULT.OK),
        map(() => undefined)
      );
  }

  public delete(scope: PartnerUsersScope, { id }: PartnerUser): Observable<void> {
    return this.partnerService.deletePartnerUser(scope, id).pipe(
      tap(() => this.notificationService.notify('partners.users.delete.success', 'common.ok')),
      catchError((error: AppError) => {
        this.notificationService.error(error.message);

        return EMPTY;
      })
    );
  }

  private open<D, R>(component: ComponentType<unknown>, data: D): Observable<R> {
    return this.dialog
      .open<unknown, D, R>(component, { ...RMS_DIALOG_MD_CONFIG, data })
      .afterClosed()
      .pipe(filter((result): result is R => !!result));
  }
}
